import { useCallback, useRef, useState } from 'react';

import { IconButton, useTheme } from '@mui/material';

import type { OwnershipOption } from 'api/data/ownership/models/OwnershipOption';
import { OwnershipAvatar } from 'components/data-display/avatar/OwnershipAvatar';
import { type InputPropertyBaseProps } from 'types/property/InputPropertyBaseProps';

import { OwnershipSelector } from './OwnershipSelector';

export interface InputPropertyOwnershipProps extends InputPropertyBaseProps {
  templateNodeId: string;
  propertyId: string;
}

export function InputPropertyOwnership({
  onBlur,
  onValueChange,
  templateNodeId,
  propertyId,
  value,
  label,
  disabled
}: InputPropertyOwnershipProps) {
  const theme = useTheme();

  const [open, setOpen] = useState<boolean>(false);

  const iconButtonRef = useRef<HTMLButtonElement | null>(null);

  const onSelectionChange = useCallback(
    (option?: OwnershipOption) => {
      onValueChange?.(option?.userName ?? '');
      onBlur?.();
      setOpen(false);
    },
    [onBlur, onValueChange]
  );

  const onClose = useCallback(() => {
    setOpen(false);
  }, []);

  return (
    <>
      <IconButton
        onClick={() => setOpen((isOpen) => !isOpen)}
        size='medium'
        ref={iconButtonRef}
        disabled={disabled}
        aria-label={label}
      >
        <OwnershipAvatar userName={value} color={theme.palette.grey['A400']} tooltip={!open} />
      </IconButton>

      {open && (
        <OwnershipSelector
          key={value}
          onChange={onSelectionChange}
          onClose={onClose}
          anchorEl={iconButtonRef.current}
          templateNodeId={templateNodeId}
          propertyId={propertyId}
          value={value}
        />
      )}
    </>
  );
}
